import queryString from 'query-string';
import DocumentationIcon from '@material-ui/icons/HelpOutline';
import {
  DOCUMENTATION_API_VIEW_TYPE,
  EDIT_PROFILE_TYPE,
} from './ViewService';

import Route from './Route';

const DOCUMENTATION_ROUTE_ID = 'documentation';
const ROOT_ROUTE_ID = 'root';

const sameArrays = (ar1, ar2) => {
  if (!ar1) return !ar2;
  if (!ar2) return false;
  if (ar1.length !== ar2.length) return false;
  return !ar1.find((o1, i) => ar2[i] !== o1);
};

const getNewArrayIfDifferent = (newArray, oldArray) => {
  return sameArrays(newArray, oldArray) ? oldArray : newArray;
};

const DEFAULT_ROUTES = [
  {
    id: DOCUMENTATION_ROUTE_ID,
    path: '/doc',
    label: 'Help',
    tooltip: 'Documentation',
    icon: DocumentationIcon,
    children: [
      {
        id: 'API', label: 'API', path: '/api', Main: DOCUMENTATION_API_VIEW_TYPE,
      },
    ],
    trailingMenu: true,
  },
];

const matchWithoutQueryString = (to, matchChildPaths) => {
  let path = to;
  // strip the to location trailing query string
  const index = to.indexOf('?');
  if (index > 0) {
    path = to.substring(0, index);
  }
  return (match, location) => {
    return location.pathname === path || (matchChildPaths && (location.pathname.indexOf(path) === 0));
  };
};
class RouteService {
  constructor(configuration, props) {
    this.appParams = {};
    this.viewService = configuration.viewService;
    const { viewService, jsonConfig } = configuration;
    const prefix = configuration.getPrefix();

    const getConfig = (name, defaultProp) => {
      if (jsonConfig && (jsonConfig[name] !== undefined)) {
        return jsonConfig[name];
      }
      return defaultProp;
    };
    // Initialize login path
    const rcfg = jsonConfig && jsonConfig.services && jsonConfig.services.routes;
    this.loginPath = rcfg && rcfg.loginPath || '/login';
    this.loginRefererQuery = rcfg && rcfg.loginRefererQuery;

    this.startPopulateRoutes(configuration, props);
    this.rootRoute = new Route({ path: prefix, id: ROOT_ROUTE_ID });
    this.rootRoute.getFullPath = (query) => {
      const q = query;
      Object.assign(q, this.appParams);
      return prefix;
    };
    this.docRoutes = getConfig('docRoutes', []);
    const processRoute = (routeCfg, parentRoute) => {
      const route = this.createRoute(this.processRouteConfig(routeCfg), parentRoute);
      let childRouteCfgs = this.getRouteChildConfigs(route, routeCfg);
      if (!childRouteCfgs) {
        return route;
      }
      if (routeCfg.id === DOCUMENTATION_ROUTE_ID) {
        const docCfgs = getConfig('docRoutes', []);
        if (docCfgs.length) {
          childRouteCfgs = docCfgs.map((docCfg) => {
            const cfg = Object.assign({}, docCfg);
            cfg.configuration = Object.assign({}, cfg.configuration, { url: cfg.configuration ? `${prefix}${cfg.configuration.url}` : null });
            return cfg;
          }).concat(childRouteCfgs);
        }
      }
      const childRoutes = childRouteCfgs.map(childCfg => processRoute(childCfg, route));
      route.setChildren(childRoutes);
      return route;
    };
    const routeCfgs = (jsonConfig && jsonConfig.routes) || this.getDefaultRoutes();
    this.routes = routeCfgs.map(route => processRoute(route, this.rootRoute));
    delete this.docRoutes;
    // If edit profile
    if (this.getEditProfilePath()) {
      const profileCmp = viewService.getComponent(EDIT_PROFILE_TYPE);
      if (profileCmp) {
        this.routes.push(new Route({
          path: this.getEditProfilePath(),
          matchPath: this.getEditProfilePath(),
          Main: profileCmp,
          drawer: false,
          id: 'editProfile',
        }, null, []));
      }
    }
    this.stopPopulateRoutes(configuration, props);
  }

  startPopulateRoutes(/* configuration, props */) {
  }

  stopPopulateRoutes(/* configuration, props */) {
  }

  getDefaultRoutes() {
    return DEFAULT_ROUTES;
  }

  createRoute(routeCfg, parentRoute) {
    return new Route(routeCfg, parentRoute);
  }

  processRouteConfig({ icon, label, ...cfgRest }) {
    const components = {};
    let mainCompType;
    ['Main', 'appBar'].forEach((key) => {
      const compType = cfgRest[key];
      if (compType && (typeof compType === 'string')) {
        if (key === 'Main') {
          mainCompType = compType;
        }
        const component = this.viewService.getViewComponent(compType);
        if (!component) {
          throw new Error(`Unkown component type ${compType}`);
        }
        components[key] = component;
      }
    });
    const resolvedIcon = icon
         && (typeof icon === 'string' ? this.viewService.getIcon(icon) : icon)
         || (mainCompType && this.viewService.getViewIcon(mainCompType));
    return {
      label,
      icon: resolvedIcon || undefined,
      ...cfgRest,
      ...components,
    };
  }

  getRouteChildConfigs(route, routeCfg) {
    return routeCfg.children || [];
  }

  getRoutes() {
    return this.routes;
  }

  findRouteByIds(paths, fromRoute) {
    if (!paths || paths.length === 0) {
      return fromRoute; // Do not return this.rootNode, not ready for navigating into children
    }
    let route = fromRoute;
    let i = 0;
    if (!route) {
      route = this.routes.find(r => r.id === paths[0]);
      i = 1;
    }
    for (; (i < paths.length) && route; i += 1) {
      route = route.getRoute(paths[i]);
    }
    return route;
  }

  sortRoutesForRouter(/* props */) {
    return this.routes.filter(route => route.path).concat(this.routes.filter(route => !route.path));
  }

  getAuthorizedRoutes(authService, props) {
    // Keep same copy of processed route arrays when unchanged to optimize redraws
    // of components receiving those props
    this.filteredRoutes = getNewArrayIfDifferent(
        this.routes.filter(r => authService.acceptRoute(r)),
        this.filteredRoutes,
    );
    this.sortedRoutes = getNewArrayIfDifferent(
      this.sortRoutesForRouter(props).filter(r => authService.acceptRoute(r)),
      this.sortedRoutes,
    );
    return [this.filteredRoutes, this.sortedRoutes];
  }

  getDefaultPath(props) {
    return this.routes[0].url(props);
  }

  setAppParam(property, value) {
    this.appParams[property] = value;
  }

  getMatchingRoute(routes, location, query) {
    const r = routes || this.routes;
    let matchingRoute = null;
    r.find((route) => {
      matchingRoute = route.match(location, query);
      return matchingRoute;
    });
    return matchingRoute;
  }

  getChildRoutes(parent, props, options = { useSelfIfEmpty: true }) {
    const { authService } = props;
    let children;
    if (parent.populateChildRouteConfigs) {
      children = parent.populateChildRouteConfigs(props, options)
        .map(routeCfg => this.processRouteConfig(routeCfg))
        .map(routeCfg => this.createRoute(routeCfg, parent));
      parent.setChildren(children);
    } else {
      // eslint-disable-next-line prefer-destructuring
      children = parent.children;
    }
    if (!children.length && options.useSelfIfEmpty) {
      children = [parent];
    }
    return children.filter(route => (!options.routeInDrawer || (route.drawer === undefined) || route.drawer) && authService.acceptRoute(route));
  }

  validateLocation(location, query) {
    const queryEmpty = q => Object.keys(q).length === 0;
    return queryEmpty(query) ? location.pathname : `${location.pathname}?${queryString.stringify(query)}`;
  }

  isValidLocation(/* location, query, props */) {
    return true;
  }

  getLoginPath() {
    if (!this.loginRefererQuery) {
      return this.loginPath;
    }
    const query = queryString.stringify({ [this.loginRefererQuery]: window.location.href });
    const path = `${this.loginPath}?${query}`;
    // https://www.gurobi.com/login/?target=http%3A%2F%2Fdemos.gurobi.com%2Frmo
    return path;
  }

  getEditProfilePath() {
    return '/editprofile';
  }

  getHomeURL() {
    return '/';
  }
}

export { RouteService as default, matchWithoutQueryString, DOCUMENTATION_ROUTE_ID };
